import { formatDistanceToNow } from 'date-fns'
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Bell,
  FileEdit,
  MessageSquare,
  Package2,
  UserCog,
  UserMinus,
  UserPlus,
} from 'lucide-vue-next'
import type { Component } from 'vue'

export const useNotificationHelpers = () => {
  const getNotificationIcon = (type: string): Component => {
    const icons: Record<string, Component> = {
      TICKET_ASSIGNED: UserPlus,
      TICKET_STATUS_CHANGED: Activity,
      TICKET_COMMENT_ADDED: MessageSquare,
      SLA_BREACHED: AlertCircle,
      SLA_WARNING: AlertTriangle,
      CUSTOMER_PRODUCT_ADDED: Package2,
      CUSTOMER_PRODUCT_STATUS_CHANGED: Activity,
      CUSTOMER_MANAGER_CHANGED: UserCog,
      CUSTOMER_BUDGET_WARNING: AlertTriangle,
      CUSTOMER_MEMBER_ADDED: UserPlus,
      CUSTOMER_MEMBER_REMOVED: UserMinus,
      CUSTOMER_UPDATED: FileEdit,
      TICKET_WATCHER_STATUS_CHANGED: Activity,
      TICKET_WATCHER_COMMENT_ADDED: MessageSquare,
      TICKET_WATCHER_PRIORITY_CHANGED: AlertTriangle,
      TICKET_WATCHER_ASSIGNEE_CHANGED: UserPlus,
      TICKET_WATCHER_UPDATED: FileEdit,
      PROJECT_UPDATED: FileEdit,
      PROJECT_TICKET_ADDED: UserPlus,
    }

    return icons[type] || Bell
  }

  const getNotificationTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      TICKET_ASSIGNED: 'Ticket Assigned',
      TICKET_STATUS_CHANGED: 'Status Changed',
      TICKET_COMMENT_ADDED: 'New Comment',
      SLA_BREACHED: 'SLA Breached',
      SLA_WARNING: 'SLA Warning',
      CUSTOMER_PRODUCT_ADDED: 'Project Added',
      CUSTOMER_PRODUCT_STATUS_CHANGED: 'Project Status Changed',
      CUSTOMER_MANAGER_CHANGED: 'Manager Changed',
      CUSTOMER_BUDGET_WARNING: 'Budget Alert',
      CUSTOMER_MEMBER_ADDED: 'Member Added',
      CUSTOMER_MEMBER_REMOVED: 'Member Removed',
      CUSTOMER_UPDATED: 'Customer Updated',
      TICKET_WATCHER_STATUS_CHANGED: 'Ticket Status Changed',
      TICKET_WATCHER_COMMENT_ADDED: 'New Comment',
      TICKET_WATCHER_PRIORITY_CHANGED: 'Priority Changed',
      TICKET_WATCHER_ASSIGNEE_CHANGED: 'Assignee Changed',
      TICKET_WATCHER_UPDATED: 'Ticket Updated',
      PROJECT_UPDATED: 'Project Updated',
      PROJECT_TICKET_ADDED: 'Ticket Added to Project',
    }

    return labels[type] || type
  }

  const getActionButtonText = (type: string): string => {
    const actions: Record<string, string> = {
      TICKET_ASSIGNED: 'View Ticket',
      TICKET_STATUS_CHANGED: 'View Ticket',
      TICKET_COMMENT_ADDED: 'View Comment',
      SLA_BREACHED: 'View Ticket',
      SLA_WARNING: 'View Ticket',
      TICKET_WATCHER_STATUS_CHANGED: 'View Ticket',
      TICKET_WATCHER_COMMENT_ADDED: 'View Ticket',
      TICKET_WATCHER_PRIORITY_CHANGED: 'View Ticket',
      TICKET_WATCHER_ASSIGNEE_CHANGED: 'View Ticket',
      TICKET_WATCHER_UPDATED: 'View Ticket',
      PROJECT_UPDATED: 'View Project',
      PROJECT_TICKET_ADDED: 'View Project',
    }

    return actions[type] || 'View Details'
  }

  const isVeryRecent = (dateString: string): boolean => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    return diffInMinutes < 5
  }

  const formatTime = (dateString: string): string => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true })
  }

  return {
    getNotificationIcon,
    getNotificationTypeLabel,
    getActionButtonText,
    isVeryRecent,
    formatTime,
  }
}
